<template>
    <div class="mt-11">
        <div class="mb-4 text-center">
            <p class="text-3lg font-bold">Do you want to record shows?</p>
            <p>
                Watch from any of your devices, anytime, anywhere.
                <AppModal>
                    <template #trigger>
                        <IconCircleInfo class="inline cursor-pointer" />
                    </template>
                    <template #contents>
                        <div class="text-sm
                                font-normal
                                break-words">
                            Some video programming content is unavailable for viewing outside of your service address location.
                        </div>
                    </template>
                </AppModal>
            </p>
        </div>
        <div v-if="getServiceType === 'iptvServices'">
            <div class="service-upgrades desktop:w-142 mx-auto grid gap-4 grid-cols-1 tablet:grid-cols-2">
                <Card
                    hours="150"
                    title="Cloud DVR"
                    cta-type="button"
                    :price="cloudDVR.price"
                    :cta-button-mini="true"
                    upgrade-type="Service Upgrade"
                    :cta-button-allow-deselect="true"
                    :has-selected-a-service="hasSelectedAService"
                    :cta-button-is-selected="isServiceSelected(cloudDVR)"
                    @deselect="selectTVOptions({ equipment: { decision: streamTV.decision, selection: streamTV }})"
                    @select="selectTVOptions({ equipment: { decision: cloudDVR.decision, selection: [streamTV, cloudDVR] }})"
                />

                <Card
                    cta-type="button"
                    title="Cloud DVR Max"
                    :cta-button-mini="true"
                    :price="cloudDVRMax.price"
                    upgrade-type="Service Upgrade"
                    :force-right-align-text="false"
                    :cta-button-allow-deselect="true"
                    :has-selected-a-service="hasSelectedAService"
                    :cta-button-is-selected="isServiceSelected(cloudDVRMax)"
                    @deselect="selectTVOptions({ equipment: { decision: streamTV.decision, selection: streamTV }})"
                    @select="selectTVOptions({ equipment: { decision: cloudDVRMax.decision, selection: [streamTV, cloudDVRMax] }})"
                />
            </div>

            <AdditionalEquipment
                price-note="each"
                cta-type="dropdown"
                title="Additional Stream TV Box"
                :getServiceType="getServiceType"
                :price="extraStreamTVBoxes.price"
                upgrade-type="Additional Equipments"
                :cta-dropdown-value="extraEquipmentsCount"
                :cta-dropdown-max="extraStreamTVBoxes.max"
                icon="/assets/images/buyflow/icon-addtv.svg"
                :has-selected-a-service="hasSelectedAService"
                @cta-dropdown-changed="selectTVOptions({ extraTVBoxes: { decision: extraStreamTVBoxes.decision, selection: extraStreamTVBoxes, quantity: $event }})"
            />
        </div>

        <div v-else-if="getServiceType === 'tivoServices'">
            <div class="service-upgrades desktop:w-142 mx-auto grid gap-4 grid-cols-1" >
                <Card
                    cta-type="button"
                    :price="tivoDVR.price"
                    :cta-button-mini="true"
                    upgrade-type="Service Upgrade"
                    title="Upgrade to Ultimate DVR"
                    :force-right-align-text="false"
                    :cta-button-allow-deselect="true"
                    :has-selected-a-service="hasSelectedAService"
                    :cta-button-is-selected="isServiceSelected(tivoDVR)"
                    @deselect="selectTVOptions({ equipment: { decision: tivo.decision, selection: tivo }})"
                    @select="selectTVOptions({ equipment: { decision: tivoDVR.decision, selection: tivoDVR }})"
                />
            </div>

            <AdditionalEquipment
                v-if="!hasSelectedAService || isServiceSelected(tivo)"
                upgrade-type="Additional Equipments"
                title="Connect Additional TVs"
                icon="/assets/images/buyflow/icon-addtv.svg"
                :price="tivoSmartBox.price"
                price-note="each"
                cta-type="dropdown"
                :getServiceType="getServiceType"
                :cta-dropdown-value="extraEquipmentsCount"
                :cta-dropdown-max="tivoSmartBox.max"
                :has-selected-a-service="hasSelectedAService"
                @cta-dropdown-changed="selectTVOptions({ extraTVBoxes: { decision: tivoSmartBox.decision, selection: tivoSmartBox, quantity: $event }})"
            />

            <AdditionalEquipment
                v-if="isServiceSelected(tivoDVR)"
                upgrade-type="Additional Equipments"
                title="Connect Additional TVs"
                icon="/assets/images/buyflow/icon-addtv.svg"
                :price="tivoMini.price"
                price-note="each"
                cta-type="dropdown"
                :getServiceType="getServiceType"
                :cta-dropdown-value="extraEquipmentsCount"
                :cta-dropdown-max="tivoMini.max"
                :has-selected-a-service="hasSelectedAService"
                @cta-dropdown-changed="selectTVOptions({ extraTVBoxes: { decision: tivoMini.decision, selection: tivoMini, quantity: $event.quantity }})"
            />
        </div>
    </div>
</template>

<script>
import Card from "./Card"
import AppModal from "~/Buyflow/components/AppModal"
import IconCircleInfo from "@/atoms/icons/IconCircleInfo"
import AdditionalEquipment from './AdditionalEquipment'
import Upgrade from "../TVServices/upgrade.vue";
export default {
    name: 'DVRCards',
    components: {
        Upgrade,
        Card,
        AppModal,
        IconCircleInfo,
        AdditionalEquipment
    },
    props: {
        tvData: {
            type: Object,
            required: true,
        },
        hasSelectedAService: {
            type: Boolean,
            default: false,
        },
        isServiceSelected: {
            type: Function,
            default: () => false,
        },
        extraEquipmentsCount: {
            type: Number,
            default: 0,
        },
        getServiceType: {
            type: String,
            required: true,
            validator: val => [
                'tivoServices',
                'iptvServices',
            ].includes(val),
        },
    },
    methods: {
        selectTVOptions (options) {
            this.$emit('select-service', options)
        },
    },
    computed: {
        tivo () {
            return this.tvData.tivoServices.tivo || null
        },
        tivoDVR () {
            return this.tvData.tivoServices.tivoDVR || null
        },
        tivoSmartBox () {
            return this.tvData.tivoEquipments.tivoSmartBox || null
        },
        tivoMini () {
            return this.tvData.tivoEquipments.tivoMini || null
        },
        streamTV () {
            return this.tvData.iptvServices.streamTV || null
        },
        cloudDVR () {
            return this.tvData.iptvServices.cloudDVR || null
        },
        cloudDVRMax () {
            return this.tvData.iptvServices.cloudDVRMax || null
        },
        extraStreamTVBoxes () {
            return this.tvData.iptvEquipments.extraStreamTVBoxes || null
        },
        streamTVVideo () {
            return {
                videoId: 'xe3nLAdKIoY',
                thumbnail: '/temp/video-player/stream-tv.jpg',
                thumbnailAlt: '',
                caption: 'What is StreamTV?',
            }
        },
    },
}
</script>

<style scoped>
.card-gray {
    background: #F9F9F9;
}
</style>
